<template>
  <!-- 旧旧版踏襲: field.style があれば inline 反映 (例: margin/padding/display 等の任意カスタマイズ)
       縦 gap は親 QuestionnaireForm の space-y-6 で支配するため mb-* は付けない。 -->
  <div :id="field.name" :class="{ 'required-empty': requiredEmpty }" :style="field.style || undefined">
    <!-- ラベル + 必須/任意バッジ -->
    <label
      v-if="field.dispname"
      :for="inputId"
      class="block text-sm font-semibold text-q4cl-primary mb-1.5 leading-snug"
    >
      {{ displayName }}
      <span v-if="field.required" class="tag-required" :aria-label="$t('必須項目')">{{ $t('※必須') }}</span>
      <span v-else-if="showOptional" class="tag-optional" aria-hidden="true">{{ $t('※任意') }}</span>
    </label>

    <!-- サポートテキスト (常時表示、placeholder の代替) -->
    <div
      v-if="field.description"
      :id="descId"
      class="text-sm text-q4cl-muted mb-2 leading-relaxed"
      v-html="sanitizedDescription"
    ></div>

    <!-- 入力コンポーネント本体 -->
    <slot :input-id="inputId" :described-by="describedBy" :required-empty="requiredEmpty" />

    <!-- ヒント (補助テキスト) -->
    <p v-if="hint" :id="hintId" class="text-sm text-q4cl-muted mt-1">{{ hint }}</p>
  </div>
</template>

<script>
/**
 * FieldFrame: 全フィールド型に共通の枠 (ラベル + バッジ + サポートテキスト)
 *
 * 旧 Q4CL-client/components/RenderQuestionnaire.vue:188 を踏襲し、
 * field.required かつ未入力なら .required-empty クラスを当ててピンク背景に。
 * 値が入った瞬間に false → クラス解除 → 自動で通常背景に戻る (動的判定)。
 *
 * 子コンポーネントに渡す slot props:
 *   - inputId: input の id (label の for 対応)
 *   - describedBy: aria-describedby に入れる文字列 (description / hint の id を join)
 *   - requiredEmpty: 必須未入力か (各 input の aria-invalid に渡す用)
 */
export default {
  name: 'FieldFrame',
  props: {
    field: { type: Object, required: true },
    showOptional: { type: Boolean, default: false },
    hint: { type: String, default: '' }
  },
  setup(props) {
    const inputId = Vue.computed(() => (props.field.name || 'field') + '__input');
    const descId = Vue.computed(() => (props.field.name || 'field') + '__desc');
    const hintId = Vue.computed(() => (props.field.name || 'field') + '__hint');

    const describedBy = Vue.computed(() => {
      const ids = [];
      if (props.field.description) ids.push(descId.value);
      if (props.hint) ids.push(hintId.value);
      return ids.length ? ids.join(' ') : undefined;
    });

    const displayName = Vue.computed(() => {
      const i18n = window.i18nStore;
      return i18n ? i18n.i18n(props.field.dispname) : props.field.dispname;
    });

    // 旧旧版踏襲 (RenderQuestionnaire.vue:906-908, getMarked):
    // 配列なら \n で join (markdown 上の段落結合)、 文字列ならそのまま。
    // marked.parse で block-level (リスト・段落・コードブロック等) もサポート。
    const sanitizedDescription = Vue.computed(() => {
      try {
        const raw = props.field.description;
        if (!raw) return '';
        const joined = Array.isArray(raw) ? raw.join('\n') : raw;
        const i18n = window.i18nStore;
        const translated = i18n ? i18n.i18n(joined) : joined;
        return DOMPurify.sanitize(marked.parse(translated));
      } catch (e) {
        return props.field.description;
      }
    });

    // 旧版 isRequiredAndEmpty(field) を fieldSerializer から呼び出して動的判定
    const requiredEmpty = Vue.computed(() => {
      const fs = window.fieldSerializer;
      return fs ? fs.isRequiredAndEmpty(props.field) : false;
    });

    return {
      inputId, descId, hintId,
      describedBy, displayName, sanitizedDescription, requiredEmpty
    };
  }
};
</script>
